// File: packages/apps/workoutsmart/src/components/widgets/TimeSinceLastActivityWidget.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress, Tooltip, useTheme } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useSession } from '@platform/core/src/context/SessionContext';
import { useResources } from '@platform/core/src/context/ResourceContext';
import { differenceInSeconds, parseISO } from 'date-fns';

const TimeSinceLastActivityWidget = () => {
  const theme = useTheme();
  const { sessionId, teamName } = useSession();
  const { resources } = useResources();
  const [timeSinceLastActivity, setTimeSinceLastActivity] = useState(null);
  const intervalRef = useRef(null);

  const activeWorkoutSession = useMemo(() => {
    if (!sessionId || teamName !== 'active_workout_team') return null;
    return resources.find(r =>
      (r.type === 'workout_session' || r.resource_type === 'workout_session') &&
      r.chat_session_id === sessionId &&
      r.status === 'IN_PROGRESS'
    );
  }, [resources, sessionId, teamName]);

  // Calculate strictly off the session object from the context/server
  const lastActivityEndTime = useMemo(() => {
    if (!activeWorkoutSession) return null;

    let latestTime = null;
    // Allow up to 5 minutes in the future to account for client/server clock skew
    const futureLimit = new Date(Date.now() + 5 * 60000);

    const checkTime = (timeStr) => {
      if (!timeStr) return;
      const time = parseISO(timeStr);
      if (!isNaN(time.getTime()) && time <= futureLimit) {
        if (!latestTime || time > latestTime) {
          latestTime = time;
        }
      }
    };

    // 1. Check the session's own updated_at
    checkTime(activeWorkoutSession.updated_at);

    // 2. Deeply check all activities and their actions (sets)
    if (activeWorkoutSession.activities) {
      activeWorkoutSession.activities.forEach(activity => {
        checkTime(activity.end_time);
        checkTime(activity.start_time);
        checkTime(activity.updated_at);
        checkTime(activity.created_at);

        if (activity.actions) {
          activity.actions.forEach(action => {
            checkTime(action.end_time);
            checkTime(action.start_time);
            checkTime(action.updated_at);
            checkTime(action.created_at);
          });
        }
      });
    }

    return latestTime ? latestTime.toISOString() : null;
  }, [activeWorkoutSession]);

  const updateTime = useCallback(() => {
    if (lastActivityEndTime) {
      const now = new Date();
      const end = parseISO(lastActivityEndTime);
      const diffInSeconds = differenceInSeconds(now, end);
      
      if (diffInSeconds < 0) {
        setTimeSinceLastActivity("0:00");
        return;
      }
      
      const minutes = Math.floor(diffInSeconds / 60);
      const seconds = diffInSeconds % 60;
      const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      setTimeSinceLastActivity(formattedTime);
    } else {
      setTimeSinceLastActivity(null);
    }
  }, [lastActivityEndTime]);

  useEffect(() => {
    updateTime();
    
    if (lastActivityEndTime) {
      intervalRef.current = setInterval(updateTime, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [lastActivityEndTime, updateTime]);

  if (teamName !== 'active_workout_team') {
    return null;
  }

  if (!activeWorkoutSession) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, color: 'text.secondary' }}>
        <CircularProgress size={16} sx={{ mr: 1 }} />
        <Typography variant="caption">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Tooltip title={timeSinceLastActivity ? "Time since last set" : "No activities logged yet"} placement="left">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', p: 1, color: 'text.secondary' }}>
        <AccessTimeIcon fontSize="small" sx={{ mr: 1 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {timeSinceLastActivity || "--:--"}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default TimeSinceLastActivityWidget;